import { ActionType, IClient, ReferenceMap } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import {
    ReferralSourcePicker,
    toMutationInput,
} from "components/references/select-reference/referral-source-picker";
import { trpc } from "components/rpc/trpc";
import { IsAllowed } from "components/shared/is-allowed";
import { AutosavingInput } from "components/ui/autosaving-input";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { Action, Dispatch } from "redux";
import { Actions } from "store/actions";

interface ClientReferralSourceProps {
    client: IClient;
    onFieldChange: (fieldName: string, newValue: string) => void;
}

export const ClientReferralSource: React.FC<ClientReferralSourceProps> = props => {
    const { client, onFieldChange } = props;
    const dispatch = useDispatch<Dispatch<Action>>();
    const trpcClient = trpc.useTRPC();

    const setReferralSource = useMutation({
        ...trpcClient.clients.setReferralSource.mutationOptions(),
        onSuccess: data => {
            dispatch({
                type: Actions[Actions.UPDATE_CLIENT_REFERRAL_SOURCE],
                clientId: client.id,
                version: data.version,
                referralBusinessId: data.referralBusinessId,
                referralContactId: data.referralContactId,
                referralSourceToken: data.referralSourceToken,
            });
        },
    });

    const showNotes = useMemo(() => {
        return (
            client.referralSourceToken ||
            client.referralContactId ||
            client.referralBusinessId ||
            typeof client.referralNotes === "string"
        );
    }, [client]);

    return (
        <IsAllowed actionType={ActionType.ViewReference}>
            <InputFormBlock>
                <div className="mb-2">
                    <ReferralSourcePicker
                        parentEntityType={AdvancedSearchEntityType.Client}
                        parentId={client.id}
                        value={client.referralSourceMap}
                        onChange={(newValue: ReferenceMap) => {
                            setReferralSource.mutate({
                                clientId: client.id,
                                source: toMutationInput(newValue),
                            });
                        }}
                    />
                </div>
                {showNotes ? (
                    <div className="form-group">
                        <AutosavingInput
                            multiLine={true}
                            label="Referral Notes (optional)"
                            initial={client.referralNotes}
                            onCommit={newVal => onFieldChange("referralNotes", newVal)}
                        />
                    </div>
                ) : null}
            </InputFormBlock>
        </IsAllowed>
    );
};
